import React, { useMemo, useState, forwardRef } from 'react';
import { Form } from 'components/atoms';
import { Formik, Field } from 'formik';
import {
  FieldArrayDndFormik, FieldCustom, FieldImageGalleryUpload, FieldTextarea
} from 'components/molecules';
import * as Yup from 'yup';
import PropTypes from 'prop-types';
import { useDebounce } from 'react-use'
import { useFormikForwardRef } from 'hooks'

const ItemSchema = Yup.object().shape({
  text: Yup.string().required('Campo obrigatório'),
  name: Yup.string().required('Campo obrigatório'),
  about: Yup.string().required('Campo obrigatório'),
  image: Yup.object({
    url: Yup.string(),
    alt: Yup.string(),
  }).required('Campo obrigatório'),
});

// eslint-disable-next-line react/prop-types
const ItemForm = forwardRef(({ initialValues, onSubmit }, ref) => {
  // this is necessary for all FieldArrayDnd forms
  // in order to control the submitForm from parent
  const formRef = useFormikForwardRef(ref);

  return (
    <Formik
      ref={formRef}
      initialValues={initialValues}
      validationSchema={ItemSchema}
      onSubmit={(values, { setSubmitting }) => {
        onSubmit(values);
        setSubmitting(false);
      }}
      validateOnMount
      enableReinitialize
    >
      <Form>
        <Field
          title="Nome"
          name="name"
          info="Primeiro e último nome"
          component={FieldCustom}
          maxLetters={120}
        />
        <Field
          title="Testemunho"
          name="text"
          component={FieldTextarea}
        />
        <Field
          title="Imagem"
          name="image"
          info="Imagem de retrato"
          component={FieldImageGalleryUpload}
        />
        <Field
          title="Subtítulo"
          name="about"
          info="Descrição da pessoa, ex: estudante, alumni, investigador..."
          component={FieldCustom}
        />
      </Form>
    </Formik>
  )
})

const TestimonialsSchema = Yup.object().shape({
  data: Yup.array().of(Yup.object().shape({ text: Yup.string() }))
    .required('Campo obrigatório'),
});

const TestimonialsEditor = ({ initialProps, onChange }) => {
  const [editorProps, setEditorProps] = useState(initialProps)

  const initialValues = useMemo(() => {
    const {
      data, title
    } = initialProps;

    const dataComputed = data.map((item) => {
      const { person, text } = item
      const {
        name, image: url, about, alt
      } = person
      return {
        name,
        text,
        about,
        image: {
          alt,
          url,
        }
      }
    })

    return {
      title,
      data: dataComputed
    }
  }, [initialProps]);

  const handleFormChange = newValues => {
    const { data } = newValues;
    const { data: dataRaw } = initialProps

    const moduleData = data.map((
      {
        name,
        text,
        about,
        image: { url, alt }
      },
      index,
    ) => ({
      ...dataRaw[index],
      text,
      person: {
        name,
        about,
        image: url,
        alt,
      }
    }))

    const newProps = { ...initialProps, ...newValues, data: moduleData };
    const isValid = TestimonialsSchema.isValidSync(newValues)
    if (isValid) setEditorProps(newProps)
  };

  useDebounce(() => {
    onChange(editorProps);
  }, 200,
  [editorProps]
  )

  return (
    <Formik
      initialValues={initialValues}
      validationSchema={TestimonialsSchema}
      validate={handleFormChange}
    >
      <Form>
        <Field
          title="Título"
          name="title"
          info="Uma frase para secção"
          component={FieldCustom}
          maxLetters={120}
        />
        <Field
          name="data"
          component={FieldArrayDndFormik}
          columns={[
            {
              accessor: 'name',
              Header: 'Nome',

            },
            {
              accessor: 'text',
              Header: 'Descrição',

            }
          ]}
          rowKey="name"
          ItemForm={ItemForm}
        />
      </Form>
    </Formik>
  );
};
TestimonialsEditor.propTypes = {
  onChange: PropTypes.func.isRequired,
  initialProps: PropTypes.shape({
    title: PropTypes.string,
    data: PropTypes.arrayOf(PropTypes.shape({
      text: PropTypes.string,
      person: PropTypes.shape({
        image: PropTypes.string,
        alt: PropTypes.string,
        about: PropTypes.string,
        name: PropTypes.string,
      })
    }))
  }),
};

TestimonialsEditor.defaultProps = {
  initialProps: {
    title: '',
    data: []
  },
};

export default TestimonialsEditor;
